import { useEffect, useRef, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { MonoIcon } from '@/components/ui/MonoIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useIntegrationStore } from '@/stores/integration-store';
import type { Integration } from 'dico-types';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';

const CORE_API_URL = import.meta.env.VITE_CORE_API_BASE_URL ?? 'http://localhost:8080';

type IntegrationCategory = 'MCP Servers' | 'Communication' | 'Developer Tools' | 'Analytics';

interface ProviderIntegrationMeta {
  id: string;
  name: string;
  description: string;
  category: IntegrationCategory;
  icon: React.ReactNode;
}

interface ConnectedIntegration extends ProviderIntegrationMeta {
  installedName: string;
  accountId: string;
}

function IntegrationIcon({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'bg-background flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border',
        className
      )}
    >
      {children}
    </div>
  );
}

const INTEGRATIONS: ProviderIntegrationMeta[] = [
  {
    id: 'slack',
    name: 'Slack',
    description: 'Assign tasks directly from Slack messages and automate workflow notifications.',
    category: 'Communication',
    icon: <MonoIcon type="Slack" className="h-5 w-5" />
  },
  {
    id: 'gmail',
    name: 'Gmail',
    description: 'Turn emails into tickets and keep your inbox in sync with your workspace.',
    category: 'Communication',
    icon: <MonoIcon type="Gmail" className="h-5 w-5" />
  },
  {
    id: 'teams',
    name: 'Microsoft Teams',
    description: 'Create and manage tasks from Teams channels and conversations.',
    category: 'Communication',
    icon: <MonoIcon type="MSTeams" className="h-5 w-5" />
  },
  {
    id: 'github',
    name: 'GitHub',
    description: 'Sync issues and pull requests from GitHub and automate developer workflows.',
    category: 'Developer Tools',
    icon: <MonoIcon type="Github" className="h-5 w-5" />
  },
  {
    id: 'discord',
    name: 'Discord',
    description: 'Create and manage tasks from Discord channels and threads.',
    category: 'Communication',
    icon: <MonoIcon type="Discord" className="h-5 w-5" />
  },
  {
    id: 'custom-mcp',
    name: 'Custom MCP',
    description: 'Connect custom MCP servers to use external tools in your workspace.',
    category: 'MCP Servers',
    icon: <MonoIcon type="Terminal" className="h-5 w-5" />
  }
];

const CATEGORIES: IntegrationCategory[] = [
  'MCP Servers',
  'Communication',
  'Developer Tools',
  'Analytics'
];

function getInstalledDisplayName(account: Integration): string {
  if (account.provider === 'slack') {
    const teamName = account.config?.teamName;
    return typeof teamName === 'string' && teamName.trim().length > 0 ? teamName : 'Slack';
  }
  if (account.provider === 'discord') {
    const guildName = account.config?.guildName;
    return typeof guildName === 'string' && guildName.trim().length > 0 ? guildName : 'Discord';
  }
  if (account.provider === 'github') {
    const repoFullName = account.config?.repoFullName;
    if (typeof repoFullName === 'string' && repoFullName.trim().length > 0) return repoFullName;
    const accountLogin = account.config?.accountLogin;
    return typeof accountLogin === 'string' && accountLogin.trim().length > 0
      ? accountLogin
      : 'GitHub';
  }
  return account.provider;
}

export function IntegrationsPage() {
  const { id: workspaceId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<IntegrationCategory | 'All'>('All');

  const { accounts, loadState, load } = useIntegrationStore();
  const [discordLoading, setDiscordLoading] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const popupPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (workspaceId) load(workspaceId);
    return () => {
      if (popupPollRef.current) clearInterval(popupPollRef.current);
    };
  }, [workspaceId]);

  // Handle OAuth callback query params (?connected=discord / ?error=...)
  useEffect(() => {
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');

    if (connected) {
      const name = INTEGRATIONS.find((i) => i.id === connected)?.name ?? connected;
      toast.success(`${name} connected successfully`);
      if (workspaceId) load(workspaceId);
      setSearchParams({}, { replace: true });
    } else if (error) {
      const messages: Record<string, string> = {
        slack_denied: 'Slack authorization was cancelled.',
        slack_failed: 'Failed to connect Slack.',
        github_failed: 'Failed to connect GitHub.',
        discord_denied: 'Discord authorization was cancelled.',
        discord_failed: 'Failed to connect Discord.',
        discord_no_token: 'Discord bot token is not configured.'
      };
      toast.error(messages[error] ?? 'Authorization failed.');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  const connectedIntegrations = useMemo(() => {
    return accounts
      .map((account) => {
        const meta = INTEGRATIONS.find((i) => i.id === account.provider);
        if (!meta) return null;

        return {
          ...meta,
          installedName: getInstalledDisplayName(account),
          accountId: account.id
        } satisfies ConnectedIntegration;
      })
      .filter(Boolean) as ConnectedIntegration[];
  }, [accounts]);

  const filteredIntegrations = INTEGRATIONS.filter((integration) => {
    const matchesSearch =
      !search ||
      integration.name.toLowerCase().includes(search.toLowerCase()) ||
      integration.description.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || integration.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const groupedByCategory = CATEGORIES.reduce<Record<string, ProviderIntegrationMeta[]>>(
    (acc, cat) => {
      const items = filteredIntegrations.filter((i) => i.category === cat);
      if (items.length > 0) acc[cat] = items;
      return acc;
    },
    {}
  );

  const openPopupAndPoll = (url: string) => {
    if (popupPollRef.current) clearInterval(popupPollRef.current);
    const popup = window.open(url);
    popupRef.current = popup;
    if (!popup) return;
    popupPollRef.current = setInterval(() => {
      if (popup.closed) {
        clearInterval(popupPollRef.current!);
        popupPollRef.current = null;
        if (workspaceId) load(workspaceId);
      }
    }, 500);
  };

  const startOAuth = async (provider: 'slack' | 'github' | 'discord') => {
    if (!workspaceId) return;

    if (provider === 'discord') {
      setDiscordLoading(true);
      try {
        const res = await fetch(
          `${CORE_API_URL}/api/auth/discord/invite-url?workspace_id=${workspaceId}`
        );
        const data = (await res.json()) as { url?: string; error?: string };
        if (data.url) {
          openPopupAndPoll(data.url);
        } else {
          toast.error(data.error ?? 'Failed to generate Discord invite URL');
        }
      } catch {
        toast.error('Failed to generate Discord invite URL');
      } finally {
        setDiscordLoading(false);
      }
      return;
    }

    const url = `${CORE_API_URL}/api/auth/${provider}?workspace_id=${workspaceId}`;
    openPopupAndPoll(url);
  };

  const handleInstall = (integrationId: string) => {
    if (integrationId === 'slack') return startOAuth('slack');
    if (integrationId === 'github') return startOAuth('github');
    if (integrationId === 'discord') return startOAuth('discord');

    if (integrationId === 'gmail' || integrationId === 'teams') {
      window.alert('This integration is coming soon.');
    }
  };

  return (
    <div className="w-3xl mx-auto min-h-full flex-1 space-y-10 px-8 py-10 xl:border-x">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Integrations</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Connect your tools so your workspace can automate workflows and take action where your
          team works.
        </p>
      </div>

      <Separator />

      <div className="flex-1 overflow-auto">
        <div className="mx-auto space-y-8">
          {/* Connected */}
          {connectedIntegrations.length > 0 && (
            <section>
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Connected</span>
                  <span className="text-muted-foreground text-xs font-medium">
                    {connectedIntegrations.length}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {connectedIntegrations.map((integration) => (
                  <Button
                    key={integration.accountId}
                    variant="outline"
                    size="lg"
                    className={'gap-0.5 pl-1'}
                    onClick={() =>
                      window.location.assign(
                        `/workspace/${workspaceId}/settings/integrations/${integration.id}/${integration.accountId}`
                      )
                    }
                  >
                    <IntegrationIcon className="rounded-md border-0 bg-transparent p-0">
                      {integration.icon}
                    </IntegrationIcon>
                    {integration.installedName}
                  </Button>
                ))}
              </div>
            </section>
          )}

          {/* Filters */}
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger className="border-border hover:bg-muted flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors">
                {selectedCategory === 'All' ? 'All Integrations' : selectedCategory}
                <MonoIcon type="ChevronDown" className="text-muted-foreground h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-40">
                <DropdownMenuItem
                  onClick={() => setSelectedCategory('All')}
                  className={cn(selectedCategory === 'All' && 'font-medium')}
                >
                  All Integrations
                </DropdownMenuItem>
                {CATEGORIES.map((cat) => (
                  <DropdownMenuItem
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={cn(selectedCategory === cat && 'font-medium')}
                  >
                    {cat}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="relative max-w-xs flex-1">
              <MonoIcon
                type="Search"
                className="text-muted-foreground absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2"
              />
              <Input
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 pl-8 text-sm"
              />
            </div>
          </div>

          {/* Grouped integration cards */}
          {Object.entries(groupedByCategory).map(([category, items]) => (
            <section key={category}>
              <h2 className="mb-3 text-sm font-medium">{category}</h2>
              <div className="grid grid-cols-3 gap-2">
                {items.map((integration) => (
                  <div
                    key={integration.id}
                    className="border-border bg-card shadow-xs flex flex-col rounded-xl border p-2"
                  >
                    <IntegrationIcon className="mb-3">{integration.icon}</IntegrationIcon>
                    <p className="mb-1 text-sm font-medium">{integration.name}</p>
                    <p className="text-muted-foreground mb-4 flex-1 text-xs leading-relaxed">
                      {integration.description}
                    </p>
                    <Button
                      size="sm"
                      className="w-full"
                      disabled={integration.id === 'discord' && discordLoading}
                      onClick={() => handleInstall(integration.id)}
                    >
                      {integration.id === 'discord' && discordLoading ? 'Opening…' : 'Connect'}
                    </Button>
                  </div>
                ))}
              </div>
            </section>
          ))}

          {Object.keys(groupedByCategory).length === 0 && (
            <div className="text-muted-foreground py-12 text-center text-sm">
              {loadState === 'loading' ? 'Loading integrations…' : 'No integrations found.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
